/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from "react";
import PropTypes from "prop-types";
import { useStaticQuery, graphql } from "gatsby";

function SEO({ description, lang, meta, title, image, url, breadcrumbs }) {
  const { wp, site } = useStaticQuery(graphql`
    query {
      wp {
        allSettings {
          generalSettingsTitle
          generalSettingsDescription
        }
        crbThemeOptions {
          crbLogoHeader {
            media {
              publicUrl
            }
          }
        }
      }
      site {
        siteMetadata {
          author
          siteUrl
        }
      }
    }
  `);

  const metaDescription =
    description || wp.allSettings.generalSettingsDescription;

  return (
    <>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={metaDescription} />
      <meta
        name="google-site-verification"
        content={process.env.GATSBY_GOOGLE_SITE_VERIFICATION}
      />
      <meta property="og:title" content={title} />
      <meta
        property="og:image"
        content={image || wp.crbThemeOptions?.crbLogoHeader?.media?.publicUrl}
      />
      <meta property="og:url" content={site.siteMetadata.siteUrl + url} />
      {url && <link rel="canonical" href={site.siteMetadata.siteUrl + url} />}
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:creator" content={site.siteMetadata.author} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      {meta.map(({ name, content }) => (
        <meta name={name} content={content} key={name} />
      ))}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((item, index) => ({
            "@type": "listItem",
            position: index + 1,
            name: item.text,
            item: process.env.GATSBY_SITE_URL + item.url,
          })),
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://jenssenmobility.no/#org",
              name: "Jenssen Mobility",
              url: "https://jenssenmobility.no/",
              logo: {
                "@type": "ImageObject",
                url: "https://jenssenmobility.no/icons/icon-512x512.png?v=5792e5cbfe28e7bf83ee276f70eea351",
                width: 512,
                height: 512,
              },
              sameAs: [
                "https://www.facebook.com/jenssenturbuss",
                "https://www.instagram.com/jenssen_turbusstransport/",
                "https://no.linkedin.com/company/jenssen-mobility",
              ],
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "customer support",
                  telephone: "(+47) 67 97 30 80",
                  email: "post@jenssenmobility.no",
                  areaServed: "NO",
                  availableLanguage: ["no", "en"],
                },
              ],
            },
            {
              "@type": "WebSite",
              "@id": "https://jenssenmobility.no/#website",
              url: "https://jenssenmobility.no/",
              name: "Jenssen Mobility",
              publisher: { "@id": "https://jenssenmobility.no/#org" },
              potentialAction: {
                "@type": "SearchAction",
                target: "https://jenssenmobility.no/search?q={query}",
                "query-input": "required name=query",
              },
            },
          ],
        })}
      </script>
    </>
  );
}

SEO.defaultProps = {
  lang: `no`,
  meta: [],
  description: ``,
  image: ``,
  url: ``,
  breadcrumbs: [],
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  image: PropTypes.string,
  url: PropTypes.string,
  breadcrumbs: PropTypes.arrayOf(PropTypes.object),
};

export default SEO;
